import { useState } from 'react';

import {
  KnowledgeSources,
  SettingsDrillRow,
  SettingsGroup,
  SettingsPageFrame,
} from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Icon,
  LoadingIndicator,
  Separator,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';

import { useChatSettings } from './useChatSettings';

export const ChatSettingsPage = () => {
  const {
    tenantId,
    contextSources,
    files,
    preferences,
    resetSettings,
    saveContextSources,
    saveFiles,
  } = useChatSettings();
  const toast = useToast();
  const { t } = useTranslation();
  const knowledgeBasesEnabled = useIsPostHogFeatureFlagEnabled(
    'CHAT_KNOWLEDGE_BASES',
  );

  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleResetSettings = async () => {
    try {
      setIsResetting(true);
      await resetSettings();
      setShowResetDialog(false);
      toast.success(t('settings.chat.resetSuccess'));
    } catch {
      toast.error(t('settings.chat.resetFailed'));
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <SettingsPageFrame title={t('settings.chat.title')}>
      {isResetting ? (
        <LoadingIndicator size="xl" />
      ) : (
        <>
          <SettingsGroup title={t('settings.nav.preferences')}>
            <SettingsDrillRow
              title={t('settings.chat.outputPreferences')}
              description={
                preferences.length > 0
                  ? t(
                      preferences.length === 1
                        ? 'settings.chat.outputPreferencesSavedOne'
                        : 'settings.chat.outputPreferencesSavedMany',
                      { count: preferences.length },
                    )
                  : t('settings.chat.outputPreferencesConfigure')
              }
              to="output-preferences"
            />
            <Separator className="my-0" />
            <SettingsDrillRow
              title={t('settings.chat.customInstructions')}
              description={t('settings.chat.customInstructionsDescription')}
              to="custom-instructions"
            />
          </SettingsGroup>

          <SettingsGroup title={t('settings.chat.conversationStarters')}>
            <SettingsDrillRow
              title={t('settings.chat.conversationStarters')}
              description={t('settings.chat.conversationStartersDescription')}
              to="conversation-starters"
            />
          </SettingsGroup>

          <SettingsGroup
            title={t('settings.chat.knowledgeBase')}
            description={t('settings.chat.knowledgeBaseDescription')}
          >
            <KnowledgeSources
              tenantId={tenantId}
              initialSources={contextSources}
              initialFiles={files}
              onSave={saveContextSources}
              onSaveFiles={saveFiles}
              showFileUploads={knowledgeBasesEnabled}
            />
          </SettingsGroup>

          <div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowResetDialog(true)}
            >
              <Icon name="RotateCcw" size="auto" />
              {t('settings.chat.resetToDefault')}
            </Button>
          </div>
        </>
      )}

      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.common.resetToDefaultTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.chat.resetDialogDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetSettings}>
              {t('settings.common.reset')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPageFrame>
  );
};
